import * as _ from 'lodash-es';
import { useMemo, useState } from 'react';
import type { InputActionMeta } from 'react-select';
import type { EntitySearchTopicResult } from '../search/entitySearch/EntitySelect';
import type { TopicEntity } from '../search/entitySearch/localEntitySearch';
import localEntitySearch from '../search/entitySearch/localEntitySearch';
import TopicSelectHierarchicalOption from './TopicSelectHierarchicalOption';
import useAllTopics from './useAllTopics';
import type { AllTopicsTreeListTopic } from './useAllTopics';

type TopicHierarchicalOptionCustomExtra = {
  secondary?: string | null;
};

type TopicHierarchicalOptionExtra<T = AllTopicsTreeListTopic> = {
  isDisabled?: boolean;
  tooltip?: string | null | undefined;
  value?: T | undefined;
};

export type TopicHierarchicalOptionType<T = AllTopicsTreeListTopic> = {
  __isNew__?: boolean;
  __isLimited__?: boolean;
  isDisabled?: boolean;
  tooltip?: string | null | undefined;
  topic?: AllTopicsTreeListTopic;
} & TopicHierarchicalOptionExtra<T> &
  TopicHierarchicalOptionCustomExtra &
  Partial<Pick<EntitySearchTopicResult, 'matches' | 'secondary' | 'score'>>;

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export interface UseTopicHierarchicalSelectPropsProps<T> {
  isDisabled: (topic: AllTopicsTreeListTopic) => boolean;
  tooltip: (topic: AllTopicsTreeListTopic) => string | null | undefined;
  getExtra?: (
    topic: AllTopicsTreeListTopic,
  ) => TopicHierarchicalOptionCustomExtra;
  getValue: (topic: AllTopicsTreeListTopic) => T;
  currentValue?: string | null;
  limit?: number;
  skip?: boolean;
}

const useTopicHierarchicalSelectProps = <T>({
  isDisabled,
  tooltip,
  getExtra,
  getValue,
  currentValue,
  limit = 100,
  skip = false,
}: UseTopicHierarchicalSelectPropsProps<T>) => {
  const [inputValue, setInputValue] = useState('');
  const allTopics = useAllTopics();
  const flattenAllTopics = useAllTopics({ flatten: true });

  const currentTopic =
    currentValue && flattenAllTopics.find((topic) => topic.id === currentValue);

  const allTopicsOptions = useMemo(() => {
    if (skip) {
      return [];
    }

    const currentTopicAncestorIds = new Set(
      currentTopic ? currentTopic.ancestors.map(({ id }) => id) : [],
    );

    const flattenAllTopicOptions: TopicHierarchicalOptionType<T>[] = [];

    const fillOptions = (
      childTopics: AllTopicsTreeListTopic[] = [],
      ancestors: AllTopicsTreeListTopic[] = [],
      forced = false,
    ) => {
      if (flattenAllTopicOptions.length >= limit && !forced) {
        return;
      }

      _.sortBy(childTopics, [(topic) => (isDisabled(topic) ? 1 : -1)]).forEach(
        (topic) => {
          flattenAllTopicOptions.push({
            isDisabled: isDisabled(topic),
            tooltip: tooltip ? tooltip(topic) : undefined,
            topic: { ...topic, ancestors: topic.ancestors || ancestors },
            value: getValue(topic),
          });

          fillOptions(
            topic.childTopics,
            [...ancestors, topic],
            currentTopicAncestorIds.has(topic.id),
          );
        },
      );
    };

    fillOptions(allTopics);

    if (flattenAllTopicOptions.length < flattenAllTopics.length) {
      flattenAllTopicOptions.push({
        __isLimited__: true,
        isDisabled: true,
      });
    }

    return flattenAllTopicOptions;
  }, [
    skip,
    allTopics,
    flattenAllTopics.length,
    isDisabled,
    tooltip,
    getValue,
    limit,
    currentTopic,
  ]);

  const searchResults = useMemo(() => {
    if (skip || !inputValue) return [];
    return localEntitySearch(
      inputValue,
      {
        topic: {
          sortBy: (l, r) => {
            if (!inputValue) return -1;
            const isLeftDisabled = isDisabled(l);
            const isRightDisabled = isDisabled(r);

            if (isLeftDisabled === isRightDisabled) {
              return 0;
            }

            return isLeftDisabled ? 1 : -1;
          },
          extra: (topic) => ({
            isDisabled: isDisabled(topic),
            value: getValue(topic),
            tooltip: tooltip ? tooltip(topic) : undefined,
            ...getExtra?.(topic),
          }),
          objects: flattenAllTopics,
        },
      },
      { limit },
    ) as (TopicEntity<AllTopicsTreeListTopic> &
      TopicHierarchicalOptionExtra<T> &
      TopicHierarchicalOptionCustomExtra)[];
  }, [
    flattenAllTopics,
    getExtra,
    getValue,
    inputValue,
    isDisabled,
    limit,
    skip,
    tooltip,
  ]);

  const handleInputChange = (
    newInputValue: string,
    actionMeta: InputActionMeta,
  ) => {
    if (!['input-blur', 'menu-close'].includes(actionMeta.action)) {
      setInputValue(newInputValue);
    }
  };

  return {
    components: {
      Option: TopicSelectHierarchicalOption,
    },
    filterOption: null,
    inputValue,
    onInputChange: handleInputChange,
    options: inputValue ? searchResults : allTopicsOptions,
  };
};

export default useTopicHierarchicalSelectProps;
